import React, { ReactNode } from 'react';
import { BottomTabs, TabItem } from './BottomTabs';
import { useKeyboardDetection } from './hooks/useKeyboardDetection';
import { useSafeAreaInsets } from './hooks/useSafeAreaInsets';
import { useTabBadges } from './hooks/useTabBadges';
import { useNavigationVisibility } from './hooks/useNavigationVisibility';
import { getDefaultTabs, getContentStyle } from './utils/tabConfiguration';
import { TabContent } from './components/TabContent';
import { FloatingActionButton } from '../ui/FloatingActionButton';
import './IOSTabLayout.css';

interface IOSTabLayoutProps {
  children: ReactNode;
  customTabs?: TabItem[];
  hideTabsOnKeyboard?: boolean;
  enableTabBarBadges?: boolean;
  testID?: string;
}

/**
 * iOS Tab Layout Wrapper Component
 * 
 * Features:
 * - Integrates with React Router for navigation
 * - Handles keyboard visibility for better UX
 * - Manages tab badges dynamically
 * - Supports custom tab configurations
 * - Follows iOS design patterns and safe areas
 * - Accessibility compliant navigation
 * 
 * Refactored to use custom hooks and smaller components for better maintainability
 */
export const IOSTabLayout: React.FC<IOSTabLayoutProps> = ({
  children,
  customTabs,
  hideTabsOnKeyboard = true,
  enableTabBarBadges = true,
  testID = 'ios-tab-layout'
}) => {
  // Use custom hooks for better separation of concerns
  const { isKeyboardVisible } = useKeyboardDetection({
    hideTabsOnKeyboard,
    enabled: true
  });
  
  const { safeAreaInsets } = useSafeAreaInsets();
  const { shouldShowMobileNav } = useNavigationVisibility();
  
  const { getTabsWithBadges } = useTabBadges(
    customTabs || getDefaultTabs(),
    enableTabBarBadges
  );

  const tabs = customTabs || getDefaultTabs({}, false); // Exclude action button since we'll use FAB

  // Get tabs with current badge state
  const tabsWithBadges = getTabsWithBadges(tabs, enableTabBarBadges);
  
  // Calculate content style using utility function
  // Add both top and bottom padding for iOS safe areas
  const contentStyle = getContentStyle(
    isKeyboardVisible, 
    shouldShowMobileNav ? safeAreaInsets.bottom : 0,
    safeAreaInsets.top
  );

  return (
    <div className="ios-tab-layout" data-testid={testID}>
      {/* Main content area */}
      <TabContent 
        style={contentStyle}
        testID={testID}
      >
        {children}
      </TabContent>

      {/* Bottom Tab Navigation - Only show on mobile devices or small viewports */}
      {!isKeyboardVisible && shouldShowMobileNav && (
        <BottomTabs
          tabs={tabsWithBadges}
          testID={`${testID}-tabs`}
        />
      )}

      {/* Floating Action Button - Only show on mobile devices when nav is visible */}
      {!isKeyboardVisible && shouldShowMobileNav && (
        <FloatingActionButton
          to="/app/recipes/add"
          ariaLabel="Add recipe"
          testID="fab-add-recipe"
          bottom={100} // Position above tab bar (83px + 17px margin)
        />
      )}
    </div>
  );
};

// Re-export the useTabBadges hook for backward compatibility
export { useTabBadges } from './hooks/useTabBadges';

export default IOSTabLayout;